import { BASE_URL, networkType, SUBGRAPH_API_KEY } from "@/config";
import { getAllFizzNodesForProvider, GPUInfo } from "./status";
import { getProviderVersion } from "./version";

const SUBGRAPH_NEWTESTNET_URL =
  "https://api.goldsky.com/api/public/project_cm16apa2e540c01wz70x34xzf/subgraphs/sph-base-sepolia-new/1.0.0/gn";
const SUBGRAPH_TESTNET_URL =
  "https://gateway.thegraph.com/api/2b6ae639b99052f02452fe8e65007ee3/subgraphs/id/8XPmGxKD883S8hGW3QqrZs872FJYMM5VFx6akwVY6Nmo";

const SUBGRAPH_MAINNET_URL =
  "https://api.goldsky.com/api/public/project_cm16apa2e540c01wz70x34xzf/subgraphs/sph-base-mainnet/1.0.3/gn";

const SUBGRAPH_URL_MAP = {
  testnet: SUBGRAPH_NEWTESTNET_URL,
  mainnet: SUBGRAPH_MAINNET_URL,
};

// Helper function to fetch data from subgraph
export const fetchSubgraphData = async (
  query: string,
  variables: Record<string, any> = {}
) => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (networkType === "mainnet") {
    headers.Authorization = `Bearer ${SUBGRAPH_API_KEY}`;
  }

  const response = await fetch(SUBGRAPH_URL_MAP[networkType], {
    method: "POST",
    headers,
    body: JSON.stringify({ query, variables }),
    cache: "no-store",
  });
  const data = await response.json();
  if (data.errors) {
    throw new Error(JSON.stringify(data.errors));
  }
  return data.data;
};

// Query to get the total number of Fizz nodes
export const getTotalFizzNodesQuery = `
  query {
    globalAttribute(id: "GLOBAL") {
      totalFizzNodes
    }
  }
`;

// Query to get Fizz nodes with pagination
const getFizzNodesQuery = `
  query getFizzNodes($first: Int, $skip: Int) {
    fizzNodes(first: $first, skip: $skip, orderBy: fizzId, orderDirection: asc) {
      id
      fizzId
      providerId
      spec
      walletAddress
      rewardPerEra
    }
  }
`;

// Query to get the total number of Providers
const getTotalProvidersQuery = `
  query {
    globalAttribute(id: "GLOBAL") {
      totalProviders
    }
  }
`;

// Query to fetch Providers with pagination
const getProvidersQuery = `
  query getProviders($first: Int, $skip: Int) {
     providers(first: $first, skip: $skip, orderBy: providerId, orderDirection: asc) {
    id
    providerId
    spec
    tier
    walletAddress
    paymentsAccepted {
      id
      address
      symbol
      name
    }
    hostUri
    certificate
    status
    joinTimestamp
    rewardWallet
    earnings {
      earned
      balance
      token {
        name
      }
    }
    lastClaimedEra
    slashedAmount
    slashedEras
    fizzNodes {
      id
      walletAddress
    }
    fizzNodeCount
    activeFizzNodeCount
  }
  }
`;

// GET FIZZ NODE BY ADDRESS
const getFizzNodeByAddressQuery = `
  query getFizzNodeByAddress($address: String!) {
  fizzNodes(
    where: { walletAddress: $address }
  ) {
    id
    fizzId
    providerId
    spec
    walletAddress
    paymentsAccepted {
      id
      address
      symbol
      name
    }
    status
    joinTimestamp
    rewardWallet
    isDenied
    isPaused
    provider {
      id
      providerId
    }
    attributes {
      category
      attributeId
      units
    }
    rewardPerEra
    lastClaimedEra
    provider {
      id
      walletAddress
      spec
    }
    totalLeases
    activeLeases
    earnings {
      earned
      balance
      token {
        name
        symbol
        address
      }
      withdrawn
      }
      slashedAmount
      slashedEras
    }
  }
`;

const getCpuRewardsBreakdownQuery = `
  query getCpuRewardsBreakdown($id: String!) {
    fizzCPUResource(id: $id) {
      resourceId
      name
      tier
      multiplier
    }
  }
`;

const getGpuRewardsBreakdownQuery = `
    query getGpuRewardsBreakdown($id: String!) {
      fizzGPUResource(id: $id) {
        resourceId
        name
        tier
        multiplier
      }
    }
  
`;

const getBaseRewardsPerEraQuery = `
  query getBaseRewardsPerEra($category: String!) {
    fizzCategories(where: { name: $category }, first: 1) {
      id
      name
      registry
      baseReward
      createdAt
    }
}
`;

const getProviderDetailsQuery = `
  query getProviderDetails($id: String!) {
    provider(id: $id) {
      id
      providerId
      spec
      walletAddress
      hostUri
      status
      joinTimestamp
      fizzNodeCount
      region
      activeFizzNodeCount
    }
  }
`;

const getFizzNodeByIdQuery = `
  query getFizzNodeById($id: ID!) {
    fizzNode(id: $id) {
      id
      fizzId
      providerId
      spec
      walletAddress
      paymentsAccepted {
      id
      address
      symbol
      name
    }
    status
    joinTimestamp
    rewardWallet
    isDenied
    attributes {
      category
      attributeId
      units
    }
    rewardPerEra
    pendingRewards
    lastClaimedEra
    totalLeases
    activeLeases
    earnings {
      earned
      balance
      token {
        name
      }
    }
    slashedAmount
    slashedEras
  }
}`;

const getFizzNodeByWalletAddressQuery = `
  query getFizzNodes($walletAddress: String!) {
    fizzNodes(
      where: { walletAddress: $walletAddress }
    ) {
      id
      fizzId
      providerId
      spec
      walletAddress
      paymentsAccepted {
        id
        address
        symbol
        name
      }
      status
      joinTimestamp
      rewardWallet
      isDenied
      provider {
        id
        providerId
        walletAddress
        spec
      }
      attributes {
        category
        attributeId
        units
      }
      rewardPerEra
      pendingRewards
      lastClaimedEra
      totalLeases
      activeLeases
      earnings {
        earned
        balance
        token {
          name
        }
      }
      slashedAmount
      slashedEras
    }
  }
`;

// const getTotalFizzAttributesQuery = `
//   query {
//     globalAttribute(id: "GLOBAL") {
//     id
//     totalFizzNodes
//     totalProviders
//     totalCPUAttributes
//     totalGPUAttributes
//     totalFizzNodeRAMAttributes
//     totalStorageAttributes
//     totalFizzNodeCPUAttributes
//     totalProviderCPUAttributes
//     totalFizzNodeGPUAttributes
//     totalProviderGPUAttributes
//     totalFizzNodeStorageAttributes
//     }
//     totalRewards(id: "GLOBAL") {
//     totalFizzRewardsPerEra
//     }
//   }
// `;

// Function to aggregate region counts
export const getFizzNodeRegionAndProviderCounts = async (
  chunkSize: number = 200,
  delayTime: number = 0
) => {
  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  // Step 1: Fetch total Fizz nodes
  const { globalAttribute } = await fetchSubgraphData(getTotalFizzNodesQuery);
  const totalFizzNodes = parseInt(globalAttribute.totalFizzNodes, 10);

  const batchSize = 1000;
  const numBatches = Math.ceil(totalFizzNodes / batchSize);

  const regionCounts: Record<string, number> = {};
  const providerCounts: Record<string, number> = {};

  // Step 2: Create request functions
  const requests: Array<() => Promise<any>> = [];
  for (let i = 0; i < numBatches; i++) {
    const skip = i * batchSize;
    const variables = { first: batchSize, skip };
    requests.push(
      () =>
        fetchSubgraphData(getFizzNodesQuery, variables) as Promise<{
          fizzNodes: Array<{
            id: string;
            fizzId: string;
            providerId: string;
            spec: string;
          }>;
        }>
    );
  }

  // Step 3: Process requests in chunks
  // const chunkSize = 5;
  const results: any[] = [];
  for (let i = 0; i < requests.length; i += chunkSize) {
    const chunk = requests.slice(i, i + chunkSize).map((req) => req());
    results.push(...(await Promise.all(chunk))); // Process each chunk in parallel
    if (i + chunkSize < requests.length) {
      await delay(delayTime); // Add a 1-second delay between chunks
    }
  }

  const allFizzNodes = results.flatMap((result) => result.fizzNodes);
  // Step 4: Process fetched data and aggregate region counts
  results.forEach((result) => {
    const { fizzNodes } = result;
    fizzNodes.forEach((node: any) => {
      const specParts = node.spec.split(",");
      const regionCode = specParts[specParts.length - 1]?.trim();

      if (regionCode) {
        regionCounts[regionCode] = (regionCounts[regionCode] || 0) + 1;
      }

      if (node.providerId) {
        providerCounts[node.providerId] =
          (providerCounts[node.providerId] || 0) + 1;
      }
    });
  });

  return {
    regionCounts,
    totalFizzNodes,
    providerCounts,
    fizzNodes: allFizzNodes,
  };
};

// Function to fetch and process Providers
export const getProviders = async () => {
  const batchSize = 100;
  let skip = 0;
  const providers: Array<any> = [];

  while (true) {
    const variables = { first: batchSize, skip };
    const { providers: fetchedProviders } = await fetchSubgraphData(
      getProvidersQuery,
      variables
    );

    if (fetchedProviders.length === 0) break;

    providers.push(...fetchedProviders);
    skip += batchSize;
  }

  return providers;
};

// Fetch and process Fizz nodes for all providers
export const processProviderNodes = async (
  providers: Array<{ id: string; walletAddress: string; hostUri: string }>
) => {
  const results = await Promise.all(
    providers.map(async (provider) => {
      const { nodes, status } = await getAllFizzNodesForProvider(
        provider.hostUri
      );

      // Summing total resources
      let totalCPUs = 0;
      let totalMemory = 0;
      let totalStorage = 0;

      // Aggregating GPU information
      const gpuInfoMap: Record<string, GPUInfo> = {};

      nodes?.forEach((node) => {
        totalCPUs += node.resources.totalCPUs;
        totalMemory += node.resources.totalMemory;
        totalStorage += node.resources.totalStorage;

        node.resources.gpuInfos.forEach((gpu: GPUInfo) => {
          const key = `${gpu.vendor}-${gpu.name}`;
          if (!gpuInfoMap[key]) {
            gpuInfoMap[key] = { ...gpu };
          } else {
            gpuInfoMap[key].availableNum += gpu.availableNum;
            gpuInfoMap[key].num += gpu.num;
          }
        });
      });

      return {
        id: provider.id,
        walletAddress: provider.walletAddress,
        totalCPUs,
        totalMemory,
        totalStorage,
        gpuInfos: Object.values(gpuInfoMap).filter((gpu) => gpu.name !== ""),
        totalNodes: nodes.length,
        status,
      };
    })
  );

  return results;
};

export const decompressSpec = (input: string): object => {
  // Replace keys with the proper JSON key names
  let formattedString = input
    .replace(/n:/g, '"Name":')
    .replace(/r:/g, '"Region":')
    .replace(/a:/g, '"Attributes":')
    .replace(/k:/g, '"Key":')
    .replace(/v:/g, '"Value":');

  // Add quotes around values (excluding numbers, booleans, and already quoted values)
  formattedString = formattedString.replace(
    /:([\w\s./-]+)([},\]])/g,
    (match, p1, p2) => {
      // If the value is not already quoted and is not a number/boolean, add quotes
      if (
        !/^".*"$/.test(p1) &&
        isNaN(Number(p1)) &&
        p1 !== "true" &&
        p1 !== "false"
      ) {
        return `:"${p1.trim()}"${p2}`;
      }
      return match;
    }
  );

  // Parse the formatted string into JSON
  try {
    return JSON.parse(formattedString);
  } catch (error) {
    console.error("Error parsing JSON:", error);
    throw error;
  }
};

const normalizeStatus = (
  status: string
): "Active" | "Registered" | "Inactive" => {
  if (status === "Active") {
    return status;
  }
  return "Inactive";
};

export const getAllGateways = async () => {
  try {
    const providers = await getProviders();

    const processedProviders = await Promise.all(
      providers
        .filter(
          (provider) =>
            provider.hostUri &&
            provider.hostUri !== "localhost" &&
            provider.walletAddress.toLowerCase() !==
              "0xf8937fd60fd0068f68b508ad3e32b8cf41509eca" &&
            !provider.hostUri.includes("nebulablock")
        )
        .map(async (provider) => {
          let name, region, attrs;
          try {
            const { Name, Region, Attributes } = JSON.parse(provider.spec);
            name = Name;
            region = Region;
            attrs = Attributes;
          } catch {
            try {
              const { Name, Region, Attributes } = decompressSpec(
                provider.spec
              ) as any;
              name = Name;
              region = Region;
              attrs = Attributes;
            } catch {
              name = "";
              region = "";
              attrs = [];
            }
          }

          let totalCPUs = 0,
            totalMemory = 0,
            totalStorage = 0,
            gpuInfos: GPUInfo[] = [],
            totalNodes = 0,
            realtimeStatus = provider.status,
            version = "-";

          // if (provider.status === "Active") {
          if (
            region !== "dev-spheron" &&
            !provider.hostUri?.includes("test.com")
          ) {
            const providerResources = await processProviderNodes([
              {
                hostUri: provider.hostUri,
                id: provider.id,
                walletAddress: provider.walletAddress,
              },
            ]).then((res) => res[0]);
            totalCPUs = providerResources.totalCPUs;
            totalMemory = providerResources.totalMemory;
            totalStorage = providerResources.totalStorage;
            gpuInfos = providerResources.gpuInfos;
            totalNodes = providerResources.totalNodes;
            realtimeStatus = providerResources.status;
            version = await getProviderVersion(provider.hostUri);
          }

          return {
            id: provider.id,
            name,
            region,
            spec: provider.spec,
            walletAddress: provider.walletAddress,
            hostUri: provider.hostUri,
            status: normalizeStatus(provider.status),
            realtimeStatus,
            joinTimestamp: provider.joinTimestamp,
            version,
            totalNodes,
            resources: {
              totalCPUs,
              totalMemory,
              totalStorage,
              gpuInfos,
            },
          };
        })
    );

    return sortGateways(
      processedProviders.filter(
        (provider) =>
          provider.region !== "dev-spheron" && provider.version >= "v1.3.0"
      )
    );
  } catch (error) {
    console.error("Error in getAllGateways:", error);
    return [];
  }
};

const sortGateways = (gatewaysList: any[]) => {
  const statusPriority: Record<string, number> = {
    Active: 1,
    Inactive: 2,
  };

  return [...gatewaysList].sort((a, b) => {
    return statusPriority[a.status] - statusPriority[b.status];
  });
};

export const getFizzNodesByAddress = async (address: string) => {
  try {
    console.log("Fetching Fizz nodes for address:", address);
    const { fizzNodes } = await fetchSubgraphData(getFizzNodeByAddressQuery, {
      address: address,
    });
    console.log("Fetched Fizz nodes:", fizzNodes);
    return fizzNodes;
  } catch (error) {
    console.error("Error in getFizzNodesByAddress:", error);
    return [];
  }
};

export const getCpuRewardsBreakdown = async (id: string) => {
  try {
    console.log("Fetching CPU rewards breakdown for ID:", id);
    const { fizzCPUResource } = await fetchSubgraphData(
      getCpuRewardsBreakdownQuery,
      {
        id: id,
      }
    );
    return fizzCPUResource;
  } catch (error) {
    console.error("Error in getCpuRewardsBreakdown:", error);
    return [];
  }
};

export const getGpuRewardsBreakdown = async (id: string) => {
  try {
    console.log("Fetching GPU rewards breakdown for ID:", id);
    const { fizzGPUResource } = await fetchSubgraphData(
      getGpuRewardsBreakdownQuery,
      {
        id: id,
      }
    );

    console.log("GPU RESOURCE", fizzGPUResource);
    return fizzGPUResource;
  } catch (error) {
    console.error("Error in getGpuRewardsBreakdown:", error);
    return [];
  }
};

export const getBaseRewardsPerEra = async (category: string) => {
  try {
    const { fizzCategories } = await fetchSubgraphData(
      getBaseRewardsPerEraQuery,
      {
        category: category,
      }
    );
    return fizzCategories?.[0]?.baseReward || 0;
  } catch (error) {
    console.error("Error in getBaseRewardsPerEra:", error);
    return [];
  }
};

export const getProviderDetails = async (id: string) => {
  try {
    const { provider } = await fetchSubgraphData(getProviderDetailsQuery, {
      id: id,
    });
    return provider;
  } catch (error) {
    console.error("Error in getProviderDetails:", error);
    return [];
  }
};
export const getFizzNodeById = async (id: string) => {
  try {
    const { fizzNode } = await fetchSubgraphData(getFizzNodeByIdQuery, {
      id,
    });
    return fizzNode;
  } catch (error) {
    console.error("Error in getFizzNodeById:", error);
    return null;
  }
};

export const getFizzNodeByAddress = async (walletAddress: string) => {
  try {
    const { fizzNodes } = await fetchSubgraphData(
      getFizzNodeByWalletAddressQuery,
      {
        walletAddress,
      }
    );
    return fizzNodes[0];
  } catch (error) {
    console.error("Error in getFizzNodeByAddress:", error);
    return null;
  }
};

export const fetchFizzNodeRegionAndProviderCounts = async (
  withAnalytics: boolean = false,
  chunkSize: number = 5,
  delayTime: number = 1000
) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/analytics/get-data?withAnalytics=${withAnalytics}&chunkSize=${chunkSize}&delayTime=${delayTime}`
    );
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error in fetchFizzNodeRegionAndProviderCounts:", error);
    return null;
  }
};

// export const fetchTotalAttributes = async () => {
//   try {
//     const { globalAttribute } = await fetchSubgraphData(
//       getTotalFizzAttributesQuery
//     );
//     console.log("Test", globalAttribute);
//     return globalAttribute;
//   } catch (error) {
//     console.error("Error fetching total attributes:", error);
//     throw error;
//   }
// };
